import { useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import MonthRow from '../MonthRow';
import { useTranslation } from '../../i18n';
import { useDragContext } from '../../contexts/DragContext';
import { useCalendarDataContext } from '../../contexts/CalendarDataContext';
import { generateYearData, generateColumnHeaders, getTotalColumns } from '../../utils/calendarUtils';
import { toDateString } from '../../utils/dateUtils';
import { CELL_MIN_HEIGHT } from '../../constants/layout';

/**
 * Component that renders the calendar grid with month rows.
 * Manages year data generation, event/milestone filtering, and month height measurement.
 */
const CalendarGrid = forwardRef(function CalendarGrid({
  year,
  scaleFactor,
  visibleEvents,
  visibleMilestones,
  onYearDataChange,
  onHeightsChange,
  isSafari = false
}, ref) {
  const { t } = useTranslation();
  const { dragPreview } = useDragContext();
  const { holidays } = useCalendarDataContext();
  const monthRowRefs = useRef([]);

  // Generate year data and column headers
  const yearData = useMemo(() => generateYearData(year, {
    months: t('calendar.months'),
    monthsShort: t('calendar.monthsShort')
  }), [year, t]);
  
  const totalColumns = useMemo(() => getTotalColumns(year), [year]);
  const columnHeaders = useMemo(() => generateColumnHeaders(totalColumns, t('calendar.daysShort')), [totalColumns, t]);

  // Pre-filter events by month for performance
  const eventsByMonth = useMemo(() => {
    const byMonth = Array.from({ length: 12 }, () => []);
    
    visibleEvents.forEach(event => {
      const [startYear, startMonth] = event.startDate.split('-').map(Number);
      const [endYear, endMonth] = event.endDate.split('-').map(Number);
      
      for (let y = startYear; y <= endYear; y++) {
        const firstMonth = (y === startYear) ? startMonth : 1;
        const lastMonth = (y === endYear) ? endMonth : 12;
        
        for (let m = firstMonth; m <= lastMonth; m++) {
          if (y === year) {
            byMonth[m - 1].push(event);
          }
        }
      }
    });
    
    return byMonth;
  }, [visibleEvents, year]);

  // Pre-filter milestones by month
  const milestonesByMonth = useMemo(() => {
    const byMonth = Array.from({ length: 12 }, () => []);
    
    visibleMilestones.forEach(milestone => {
      const [msYear, msMonth] = milestone.date.split('-').map(Number);
      if (msYear === year) {
        byMonth[msMonth - 1].push(milestone);
      }
    });
    
    return byMonth;
  }, [visibleMilestones, year]);

  // Pre-compute holiday lookup map once for all months (performance optimization)
  // This was previously computed in each MonthRow (12x), now computed once here
  const holidayByDate = useMemo(() => {
    const map = new Map();
    holidays.forEach(h => {
      // Expand date range into individual dates
      const [sy, sm, sd] = h.startDate.split('-').map(Number);
      const [ey, em, ed] = h.endDate.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        map.set(toDateString(d), h);
      }
    });
    return map;
  }, [holidays]);

  // Notify parent when year data or column headers change
  useEffect(() => {
    if (onYearDataChange) {
      onYearDataChange(yearData, columnHeaders);
    }
  }, [yearData, columnHeaders, onYearDataChange]);

  // Measure month row heights
  // Changed from useLayoutEffect to useEffect to defer measurements and prevent layout thrashing during drag
  useEffect(() => {
    if (monthRowRefs.current.length === 0) return;
    
    const measureHeights = () => {
      const heights = monthRowRefs.current.map((ref) => {
        if (!ref) return CELL_MIN_HEIGHT;
        const rect = ref.getBoundingClientRect();
        // With CSS zoom, getBoundingClientRect behavior differs:
        // - Safari: returns unzoomed (base) size
        // - Chrome: returns zoomed size, need to divide by scaleFactor
        return isSafari ? rect.height : rect.height / scaleFactor;
      });
      
      if (onHeightsChange) {
        onHeightsChange(heights);
      }
    };
    
    // Defer measurement to next frame to avoid blocking paint
    let rafId;
    rafId = requestAnimationFrame(measureHeights);
    
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [yearData, scaleFactor, visibleEvents, visibleMilestones, onHeightsChange, isSafari]);

  // Expose refs to parent via useImperativeHandle
  useImperativeHandle(ref, () => ({
    monthRowRefs: monthRowRefs.current
  }));

  return (
    <>
      {/* Column headers - hidden, used for structure */}
      <div className="calendar__column-headers calendar__column-headers--hidden">
        <div className="calendar__month-spacer" />
        <div className="calendar__header-cells">
          {columnHeaders.map((header, index) => (
            <div
              key={index}
              className={`calendar__header-cell ${header.isWeekend ? 'calendar__header-cell--weekend' : ''}`}
            >
              {header.label}
            </div>
          ))}
        </div>
      </div>

      {/* Month rows */}
      <div className="calendar__months">
        {yearData.map((month, index) => (
          <MonthRow 
            key={month.index} 
            ref={el => monthRowRefs.current[index] = el}
            month={month}
            hideLabel={true}
            events={eventsByMonth[month.index]}
            milestones={milestonesByMonth[month.index]}
            dragPreview={dragPreview}
            holidayByDate={holidayByDate}
          />
        ))}
      </div>
    </>
  );
});

export default CalendarGrid;
